import offlineUtils from '../offline/offlineUtils';
import {
  DamageTemplate,
  EngineSeries,
  EngineTemplate,
  HazardousMaterial,
  Market,
  Owner,
  RailwayCompany,
  Station,
  WagonTemplate,
  Workshop,
} from '../model/templates';
import marketCache from './marketCache';
import stationCache from './stationCache';
import engineSeriesCache from './engineSeriesCache';
import engineCache from './engineCache';
import wagonCache from './wagonCache';
import hazmatCache from './hazmatCache';
import ateCache from './ateCache';
import damageCache from './damageCache';
import ownerCache from './ownerCache';
import railwayCompanyCache from './railwayCompanyCache';
import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import apiHelper from '../../api/apiHelper';
import workshopCache from './workshopCache';

type Templates = {
  series: EngineSeries[];
  engines: EngineTemplate[];
  wagons: WagonTemplate[];
  hazmats: HazardousMaterial[];
  stations: Station[];
  markets: Market[];
  ates: string[];
  owners: Owner[];
  damages: DamageTemplate[];
  companies: RailwayCompany[];
  workshops: Workshop[];
};

export type TemplatesState = {
  basicLoading: boolean;
  wagonLoading: boolean;
  basicError: boolean;
  wagonError: boolean;
};

const initialState: TemplatesState = {
  basicLoading: false,
  wagonLoading: false,
  basicError: false,
  wagonError: false,
};

// Workaround flag to avoid clearing the wagons cache, which locks Firefox for 25 seconds on each page reload
const clearWagonsCache = (localStorage.getItem('clearWagonsCache') ?? 'true') === 'true';

export const loadBasicTemplates = createAsyncThunk<void>('templates/loadBasic', async () => {
  if (!offlineUtils.isOnline()) {
    // Don't try to load the templates from the server if offline
    return;
  }

  try {
    const data: Templates = await apiHelper.get('/api/templates');
    if (data) {
      await workshopCache.saveItems(data.workshops);
      await marketCache.saveItems(data.markets);
      await engineSeriesCache.saveItems(data.series);
      await engineCache.saveItems(data.engines);
      await ateCache.saveItems(data.ates);
      await damageCache.saveItems(data.damages);
      await ownerCache.saveItems(data.owners);
      await railwayCompanyCache.saveItems(data.companies);
      await hazmatCache.saveItems(data.hazmats);
      await stationCache.saveItems(data.stations);
    }
  } catch (e) {
    if (offlineUtils.isOnline()) {
      throw e;
    } else {
      // Connection lost - ignore the error
    }
  }
});

export const loadWagonTemplates = createAsyncThunk<void>('templates/loadWagon', async () => {
  if (!offlineUtils.isOnline()) {
    // Don't try to load the templates from the server if offline
    return;
  }

  try {
    const data: Templates = await apiHelper.get('/api/templates/wagon');
    await wagonCache.saveItems(data.wagons, { clear: clearWagonsCache });
  } catch (e) {
    if (offlineUtils.isOnline()) {
      throw e;
    } else {
      // Connection lost - ignore the error
    }
  }
});

export const loadAllTemplates = createAsyncThunk<void>('templates/loadAll', async (_, { dispatch }) => {
  // Load basic templates first
  await dispatch(loadBasicTemplates()).unwrap();

  // Then load wagon templates
  await dispatch(loadWagonTemplates()).unwrap();
});

export const templatesSlice = createSlice({
  name: 'templates',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      // Basic templates
      .addCase(loadBasicTemplates.pending, (state) => {
        state.basicLoading = true;
        state.basicError = false;
      })
      .addCase(loadBasicTemplates.fulfilled, (state) => {
        state.basicLoading = false;
        state.basicError = false;
      })
      .addCase(loadBasicTemplates.rejected, (state) => {
        state.basicLoading = false;
        state.basicError = true;
      })
      // Wagon templates
      .addCase(loadWagonTemplates.pending, (state) => {
        state.wagonLoading = true;
        state.wagonError = false;
      })
      .addCase(loadWagonTemplates.fulfilled, (state) => {
        state.wagonLoading = false;
        state.wagonError = false;
      })
      .addCase(loadWagonTemplates.rejected, (state) => {
        state.wagonLoading = false;
        state.wagonError = true;
      });
  },
});
export default templatesSlice.reducer;
